<template>
  <div>
    <table :class="$style.generalTable">
      <tbody>
        <tr>
          <td :class="$style.name">
            {{ general.mainObject.key }}:
          </td>
          <td :class="$style.valueText">
            <component :is="headerTag(general.mainObject.key)" :class="$style.valueTextSpan">
              {{ general.mainObject.value }}
            </component>
          </td>
        </tr>
        <tr>
          <td :class="$style.name">
            Full analysis:
          </td>
          <td :class="$style.valueText">
            <a :class="$style.valueTextSpan" :href="general.fullAnalysis" target="_blank">{{
              general.fullAnalysis
            }}</a>
          </td>
        </tr>
        <tr v-if="general.verdict">
          <td :class="[$style.name, $style.verdictKey]">
            Verdict:
          </td>
          <td>
            <Badge
              :class="$style.valueTextSpan"
              :type="general.verdict.type"
              :value="general.verdict.value"
            />
          </td>
        </tr>
        <tr v-if="general.threats.length">
          <td :class="$style.name">
            Threats:
          </td>
          <td>
            <TabsViewer :values="general.threats">
              <template #after="props">
                <div :class="$style.threatsLinkCtn">
                  <a
                    target="_blank"
                    :href="general.malwareTrendsTrackerUrl + general.threats[props.index].mainTag"
                    :class="$style.threatsLink"
                  >Malware Trends Tracker&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>>></a>
                </div>
              </template>
            </TabsViewer>
          </td>
        </tr>
        <tr>
          <td :class="$style.name">
            Analysis date:
          </td>
          <td :class="$style.valueText">
            <span :class="$style.valueTextSpan">
              {{ computedAnalysisDate }}
            </span>
          </td>
        </tr>
        <tr>
          <td :class="$style.name">
            OS:
          </td>
          <td :class="$style.valueText">
            <span :class="$style.valueTextSpan">
              {{ general.os }}
            </span>
          </td>
        </tr>
        <tr v-if="general.tags.length">
          <td :class="$style.name">
            Tags:
          </td>
          <td>
            <div :class="$style.tags">
              <div v-for="(value, i) in general.tags" :key="i" :class="$style.tag">
                {{ value }}
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="general.indicators.length">
          <td :class="$style.name">
            Indicators:
          </td>
          <td>
            <div :class="$style.indicators">
              <template v-for="(indicator, index) in general.indicators" :key="index">
                <Indicator :name="indicator" />
              </template>
            </div>
          </td>
        </tr>
        <tr v-if="general.mime">
          <td :class="$style.name">
            MIME:
          </td>
          <td :class="$style.valueText">
            <span :class="$style.valueTextSpan">
              {{ general.mime }}
            </span>
          </td>
        </tr>
        <tr v-if="general.fileInfo">
          <td :class="$style.name">
            File info:
          </td>
          <td :class="$style.valueText">
            <span :class="$style.valueTextSpan">
              {{ general.fileInfo }}
            </span>
          </td>
        </tr>
        <tr v-for="hash in general.hashes" :key="hash.key">
          <td :class="$style.name">
            {{ hash.key }}:
          </td>
          <td :class="$style.valueText">
            <component :is="headerTag(hash.key)" :class="$style.valueTextSpan">
              {{ hash.value }}
            </component>
          </td>
        </tr>
      </tbody>
    </table>

    <TipMessage :class="$style.tipMessage">
      <a href="https://any.run" target="_blank">ANY.RUN</a> is an interactive service which provides
      full access to the guest system. Information in this report could be distorted by user actions
      and is provided for user acknowledgement as it is.
      <a href="https://any.run" target="_blank">ANY.RUN</a> does not guarantee maliciousness or
      safety of the content.
    </TipMessage>

    <AccordeonPanel
      :start-open="false"
      :class="$style.softCtn"
      title="Software environment set and analysis options"
    >
      <div :class="$style.softBody">
        <h4 :class="[com.title, $style.softTitle]">
          Launch configuration
        </h4>

        <dl :class="$style.keyValueGrid">
          <template
            v-for="(nameValue, index) in general.launchConfiguration.nameValues"
            :key="index"
          >
            <dt :class="com.keyValueName">
              {{ nameValue.name }}:
            </dt>
            <dd :class="com.keyValueValue">
              {{ onOffFormat(nameValue.value) }}
            </dd>
          </template>
        </dl>

        <div :class="$style.lists">
          <div v-for="(list, index) in general.launchConfiguration.lists" :key="index">
            <h5 :class="$style.listTitle">
              {{ list.title }}
            </h5>
            <ul :class="com.list">
              <li v-for="(value, index) in list.values" :key="index">
                {{ value }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </AccordeonPanel>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import AccordeonPanel from './AccordeonPanel.vue';
import Badge from './Badge.vue';
import Indicator from './Indicator.vue';
import TabsViewer from './TabsViewer.vue';
import TipMessage from './TipMessage.vue';

export default defineComponent({
  components: {
    Badge,
    TabsViewer,
    TipMessage,
    AccordeonPanel,
    Indicator,
  },
  inject: ['root'],
  data: () => ({
    localAnalysisDate: null,
  }),
  computed: {
    general() {
      return this.root.general;
    },
    computedAnalysisDate() {
      return this.localAnalysisDate || this.general.analysisDate;
    },
  },
  mounted() {
    this.localAnalysisDate = new Intl.DateTimeFormat('en-US', {
      hour12: false,
      month: 'long',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(this.general.rawAnalysisDate);
  },
  methods: {
    headerTag(key) {
      if (key === 'URL') return 'h1';
      if (key === 'SHA256' && this.general.mainObject.key !== 'URL') {
        return 'h1';
      }
      return 'h2';
    },
    onOffFormat(value) {
      if (value === true) return 'on';
      if (value === false) return 'off';
      return value;
    },
  },
});
</script>

<style module lang="scss">
.generalTable {
  margin-top: 20px;
  tr + tr td {
    padding-top: 9.3px;
  }
  td:last-child {
    width: 100%;
  }
}
.generalTable + .tipMessage {
  margin-top: 15px;
}
.threatsLinkCtn {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  @media print {
    display: none;
  }
}
.threatsLink {
  white-space: pre;
  font-weight: 500;
  font-size: 12px;
  color: rgba(47, 53, 67, 0.5);
}
.name {
  vertical-align: top;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.02em;

  color: rgba(63, 71, 87, 0.85);
  padding-right: 35px;
  white-space: nowrap;
}
.verdictKey {
  vertical-align: middle;
}
.valueText {
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: #2f3543;
  line-height: 130%;
  a {
    border-top: 1px solid transparent;
    border-bottom: 1px solid #000;
  }
}
.valueTextSpan {
  vertical-align: bottom;

  @at-root {
    h2#{&},
    h1#{&} {
      font-weight: inherit;
      font-size: inherit;
      font-style: inherit;
      font-family: inherit;
    }
  }
}
.tags,
.indicators {
  display: flex;
  flex-wrap: wrap;
  & > * {
    margin-left: 4px;
    margin-bottom: 4px;
  }
  margin-left: -4px;
  margin-bottom: -4px;
}
.tag {
  display: inline-block;
  padding: 0 8px;
  font-size: 12px;
  color: #2f3543;
  box-sizing: content-box;

  border: 1px solid #2f3543;
  border-radius: 8px;
}

.indicator {
  width: 14px;
  height: 14px;
}

.keyValueGrid {
  display: grid;
  grid-template-columns: repeat(3, auto 1fr);
  grid-auto-flow: row;
  grid-row-gap: 10px;

  @media screen and (max-width: 920px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media screen and (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  & > *:nth-child(odd) {
    padding-right: 10px;
  }
}

.keyValueItem {
  display: flex;
}

.tipMessage + .softCtn {
  margin-top: 30px;
}

.listTitle {
  display: block;
  font-weight: 500;
  font-size: 18px;
  color: #3f4757;
  padding: 4px 0;
  box-sizing: content-box;
  border-bottom: 1px solid transparent;
  border-top: 1px solid transparent;

  position: relative;

  & + ul {
    margin-top: 12px;
  }

  &::after {
    content: '';
    width: 100%;
    position: absolute;
    display: block;
    height: 1px;
    top: 100%;
    background-color: rgba(63, 71, 87, 0.7);
    border-radius: 0.5px;
    pointer-events: none;
  }
}

.lists {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  grid-gap: 40px;

  @media screen and (max-width: 700px) {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
  }

  li {
    & + li {
      margin-top: 10px;
    }
  }
}

.softBody {
  padding: 20px;
}

.softTitle {
  display: block;
  border-bottom: 2px solid transparent;
  padding-bottom: 2px;
  position: relative;

  & + .keyValueGrid {
    margin-top: 20px;
  }

  &::after {
    content: '';
    width: 100%;
    position: absolute;
    display: block;
    height: 2px;
    top: 100%;
    background-color: #3f4757;
    border-radius: 1px;
    pointer-events: none;
  }

  & ~ * {
    margin-left: 10px;
  }
}
.keyValueGrid + .lists {
  margin-top: 30px;
}
</style>

<style lang="scss" src="./common.module.scss" module="com"></style>
